import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {apiClient} from 'utils/clients';
import {AccountConfigModeType} from 'modules/administration/components/ai-settings/types';

export const GLOBAL_AI_FEATURES_KEY = 'globalAiFeatures';
const GLOBAL_AI_FEATURES_ADMIN_ROLES = new Set(['Role-1', 'Role-2']);

export const canManageGlobalAiFeatures = (userRole?: string) =>
    Boolean(userRole && GLOBAL_AI_FEATURES_ADMIN_ROLES.has(userRole));

type GlobalAiFeaturesConfigResponse = {
    config: {
        globalAiFeatures?: boolean;
    };
};

const globalAiFeaturesQueryKey = (accountId: string) =>
    ['GLOBAL-AI-FEATURES', accountId] as const;

export const useGlobalAiFeaturesConfig = ({
    accountId,
    enabled,
}: {
    accountId: string;
    enabled: boolean;
}) =>
    useQuery({
        queryKey: globalAiFeaturesQueryKey(accountId),
        enabled: enabled && Boolean(accountId),
        queryFn: () =>
            apiClient
                .get<GlobalAiFeaturesConfigResponse>(
                    `${import.meta.env.VITE_WLM_BACKEND_URL}/accounts/${accountId}/config/v1/account-config`,
                    {params: {include: GLOBAL_AI_FEATURES_KEY}},
                )
                .then(({data}) => data),
    });

export const useSetGlobalAiFeaturesConfig = ({accountId}: {accountId: string}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['GLOBAL-AI-FEATURES', 'SET', accountId],
        mutationFn: (enabled: boolean) =>
            apiClient.post(
                `${import.meta.env.VITE_WLM_BACKEND_URL}/accounts/${accountId}/config/v1/account-config`,
                {config: {[GLOBAL_AI_FEATURES_KEY]: enabled}},
            ),
        onSuccess: (_response, enabled) => {
            queryClient.setQueryData<GlobalAiFeaturesConfigResponse>(
                globalAiFeaturesQueryKey(accountId),
                {config: {[GLOBAL_AI_FEATURES_KEY]: enabled}},
            );
        },
    });
};

export const useConfigureAccountAi = ({accountId}: {accountId: string}) =>
    useMutation({
        mutationKey: ['GLOBAL-AI-FEATURES', 'CONFIGURE-AI', accountId],
        mutationFn: (mode: AccountConfigModeType.DISABLED | AccountConfigModeType.NETAPP_MODEL_DEFAULT) =>
            apiClient.post(
                `${import.meta.env.VITE_WLM_BACKEND_URL}/accounts/${accountId}/ai-limiter/v1/account-configuration`,
                {mode: {type: mode}},
            ),
    });
